// Trustline validation service
import { trustline, TrustlineInitOptions, TrustlineWeb3ValidateParams, TrustlineValidateResponse } from '@trustline.id/websdk';
import { ethers } from 'ethers';
import { getTrustlineComplianceModuleAddress } from '../contracts/contract';

class TrustlineService {
  private isInitialized = false;

  constructor() {
    this.initialize();
  }

  // NOTE: Currently only validateWithdraw is active
  // validateDeposit and validateRefund are commented out in components but kept for future use

  private initialize() {
    try {
      const initOptions: TrustlineInitOptions = {
        clientId: process.env.REACT_APP_TRUSTLINE_CLIENT_ID || 'YOUR_TRUSTLINE_CLIENT_ID',
        loginUri: process.env.REACT_APP_TRUSTLINE_LOGIN_URI || 'https://yourapp.com/auth/trustline/callback'
      };
      trustline.init(initOptions);
      this.isInitialized = true;
      console.log('Trustline SDK initialized');
    } catch (error) {
      console.error('Failed to initialize Trustline SDK:', error);
    }
  }

  async validateTransaction(data: TrustlineWeb3ValidateParams): Promise<TrustlineValidateResponse> {
    if (!this.isInitialized) {
      throw new Error('Trustline SDK not initialized');
    }

    try {
      const response = await trustline.validate(data);
      return response;
    } catch (error) {
      console.error('Trustline validation failed:', error);
      throw error;
    }
  }

  async validateDeposit(
    chainId: number,
    senderAddress: string,
    contractAddress: string,
    tokenAddress: string,
    amount: string,
    beneficiary: string
  ): Promise<boolean> {
    try {
      const validationData: TrustlineWeb3ValidateParams = {
        chainId: chainId.toString(),
        senderAddress,
        contractAddress,
        nativeAmount: tokenAddress === '0x0000000000000000000000000000000000000000' ? amount : '0',
        data: {
          functionSelector: 'depose(address,address,uint256)',
          args: [tokenAddress, beneficiary, amount]
        }
      };

      const response = await this.validateTransaction(validationData);

      if ('error' in response) {
        console.error('Trustline validation error:', response.error);
        return false;
      }

      switch (response.result.status) {
        case 'approved':
          console.log('Transaction approved by Trustline');
          return true;
        case 'approval_required':
          console.log('Transaction requires manual approval');
          return false;
        case 'rejected':
          console.log('Transaction rejected by Trustline:', response.result.reason);
          return false;
        default:
          console.log('Unknown validation status:', (response.result as any).status);
          return false;
      }
    } catch (error) {
      console.error('Trustline validation failed:', error);
      return false;
    }
  }

  async validateWithdraw(
    chainId: number,
    senderAddress: string,
    contractAddress: string,
    depositId: number
  ): Promise<boolean> {
    try {
      const validationData: TrustlineWeb3ValidateParams = {
        chainId: chainId.toString(),
        senderAddress,
        contractAddress,
        nativeAmount: '0',
        data: {
          functionSelector: 'withdraw(uint256)',
          args: [depositId.toString()]
        }
      };

      const response = await this.validateTransaction(validationData);

      if ('error' in response) {
        console.error('Trustline validation error:', response.error);
        return false;
      }

      switch (response.result.status) {
        case 'approved':
          console.log('Withdraw transaction approved by Trustline');
          return true;
        case 'approval_required':
          console.log('Withdraw transaction requires manual approval');
          return false;
        case 'rejected':
          console.log('Withdraw transaction rejected by Trustline:', response.result.reason);
          return false;
        default:
          console.log('Unknown validation status:', (response.result as any).status);
          return false;
      }
    } catch (error) {
      console.error('Trustline validation failed:', error);
      return false;
    }
  }

  async validateRefund(
    chainId: number,
    senderAddress: string,
    contractAddress: string,
    depositId: number
  ): Promise<boolean> {
    try {
      const validationData: TrustlineWeb3ValidateParams = {
        chainId: chainId.toString(),
        senderAddress,
        contractAddress,
        nativeAmount: '0',
        data: {
          functionSelector: 'refund(uint256)',
          args: [depositId.toString()]
        }
      };

      const response = await this.validateTransaction(validationData);

      if ('error' in response) {
        console.error('Trustline validation error:', response.error);
        return false;
      }

      switch (response.result.status) {
        case 'approved':
          console.log('Refund transaction approved by Trustline');
          return true;
        case 'approval_required':
          console.log('Refund transaction requires manual approval');
          return false;
        case 'rejected':
          console.log('Refund transaction rejected by Trustline:', response.result.reason);
          return false;
        default:
          console.log('Unknown validation status:', (response.result as any).status);
          return false;
      }
    } catch (error) {
      console.error('Trustline validation failed:', error);
      return false;
    }
  }

  // ERC3643 Token Operations Validation


  // Helper functions to build context data matching the compliance module
  private buildTransferContext(token: string, from: string, to: string, value: string): string {
    // Equivalent to: abi.encodePacked("TRANSFER:", token, from, to, value)
    const prefix = "TRANSFER:";
    const tokenBytes = ethers.getBytes(token);
    const fromBytes = ethers.getBytes(from);
    const toBytes = ethers.getBytes(to);
    const valueBytes = ethers.getBytes(ethers.zeroPadValue(ethers.toBeHex(value), 32));
    
    return ethers.hexlify(ethers.concat([
      ethers.toUtf8Bytes(prefix),
      tokenBytes,
      fromBytes,
      toBytes,
      valueBytes
    ]));
  }

  private buildMintContext(token: string, to: string, value: string): string {
    // Equivalent to: abi.encodePacked("MINT:", token, address(0), to, value)
    const prefix = "MINT:";
    const tokenBytes = ethers.getBytes(token);
    const zeroAddressBytes = ethers.getBytes("0x0000000000000000000000000000000000000000");
    const toBytes = ethers.getBytes(to);
    const valueBytes = ethers.getBytes(ethers.zeroPadValue(ethers.toBeHex(value), 32));
    
    return ethers.hexlify(ethers.concat([
      ethers.toUtf8Bytes(prefix),
      tokenBytes,
      zeroAddressBytes,
      toBytes,
      valueBytes
    ]));
  }

  private buildBurnContext(token: string, from: string, value: string): string {
    // Equivalent to: abi.encodePacked("BURN:", token, from, address(0), value)
    const prefix = "BURN:";
    const tokenBytes = ethers.getBytes(token);
    const fromBytes = ethers.getBytes(from);
    const zeroAddressBytes = ethers.getBytes("0x0000000000000000000000000000000000000000");
    const valueBytes = ethers.getBytes(ethers.zeroPadValue(ethers.toBeHex(value), 32));
    
    return ethers.hexlify(ethers.concat([
      ethers.toUtf8Bytes(prefix),
      tokenBytes,
      fromBytes,
      zeroAddressBytes,
      valueBytes
    ]));
  }

  async validateTransfer(
    chainId: number,
    senderAddress: string,
    contractAddress: string,
    from: string,
    to: string,
    amount: string,
    provider: ethers.Provider
  ): Promise<boolean> {
    try {
      // Get the Trustline compliance module address
      const complianceModuleAddress = await getTrustlineComplianceModuleAddress(provider, contractAddress);
      
      // Build raw ABI-encoded data matching the compliance module
      const contextData = this.buildTransferContext(contractAddress, from, to, amount);
      
      const validationData: TrustlineWeb3ValidateParams = {
        chainId: chainId.toString(),
        senderAddress: from, // The actor/subject is the sender (from address)
        contractAddress: complianceModuleAddress, // Use compliance module address
        nativeAmount: '0',
        data: contextData, // Raw ABI-encoded context data
        validationMode: 'erc3643'
      };

      const response = await this.validateTransaction(validationData);

      if ('error' in response) {
        console.error('Trustline validation error:', response.error);
        return false;
      }

      switch (response.result.status) {
        case 'approved':
          console.log('Transfer transaction approved by Trustline');
          return true;
        case 'approval_required':
          console.log('Transfer transaction requires manual approval');
          return false;
        case 'rejected':
          console.log('Transfer transaction rejected by Trustline:', response.result.reason);
          return false;
        default:
          console.log('Unknown validation status:', (response.result as any).status);
          return false;
      }
    } catch (error) {
      console.error('Trustline validation failed:', error);
      return false;
    }
  }

  async validateTransferFrom(
    chainId: number,
    senderAddress: string,
    contractAddress: string,
    from: string,
    to: string,
    amount: string,
    provider: ethers.Provider
  ): Promise<boolean> {
    try {
      // Get the Trustline compliance module address
      const complianceModuleAddress = await getTrustlineComplianceModuleAddress(provider, contractAddress);
      
      // Build raw ABI-encoded data matching the compliance module
      const contextData = this.buildTransferContext(contractAddress, from, to, amount);
      
      const validationData: TrustlineWeb3ValidateParams = {
        chainId: chainId.toString(),
        senderAddress: from, // The actor/subject is the from address
        contractAddress: complianceModuleAddress, // Use compliance module address
        nativeAmount: '0',
        data: contextData, // Raw ABI-encoded context data
        validationMode: 'erc3643'
      };

      const response = await this.validateTransaction(validationData);

      if ('error' in response) {
        console.error('Trustline validation error:', response.error);
        return false;
      }

      switch (response.result.status) {
        case 'approved':
          console.log('TransferFrom transaction approved by Trustline');
          return true;
        case 'approval_required':
          console.log('TransferFrom transaction requires manual approval');
          return false;
        case 'rejected':
          console.log('TransferFrom transaction rejected by Trustline:', response.result.reason);
          return false;
        default:
          console.log('Unknown validation status:', (response.result as any).status);
          return false;
      }
    } catch (error) {
      console.error('Trustline validation failed:', error);
      return false;
    }
  }

  async validateMint(
    chainId: number,
    senderAddress: string,
    contractAddress: string,
    to: string,
    amount: string,
    provider: ethers.Provider
  ): Promise<boolean> {
    try {
      // Get the Trustline compliance module address
      const complianceModuleAddress = await getTrustlineComplianceModuleAddress(provider, contractAddress);
      
      // Build raw ABI-encoded data matching the compliance module
      const contextData = this.buildMintContext(contractAddress, to, amount);
      
      const validationData: TrustlineWeb3ValidateParams = {
        chainId: chainId.toString(),
        senderAddress: to, // The actor/subject is the recipient (to address)
        contractAddress: complianceModuleAddress, // Use compliance module address
        nativeAmount: '0',
        data: contextData, // Raw ABI-encoded context data
        validationMode: 'erc3643'
      };

      const response = await this.validateTransaction(validationData);

      if ('error' in response) {
        console.error('Trustline validation error:', response.error);
        return false;
      }

      switch (response.result.status) {
        case 'approved':
          console.log('Mint transaction approved by Trustline');
          return true;
        case 'approval_required':
          console.log('Mint transaction requires manual approval');
          return false;
        case 'rejected':
          console.log('Mint transaction rejected by Trustline:', response.result.reason);
          return false;
        default:
          console.log('Unknown validation status:', (response.result as any).status);
          return false;
      }
    } catch (error) {
      console.error('Trustline validation failed:', error);
      return false;
    }
  }

  async validateBurn(
    chainId: number,
    senderAddress: string,
    contractAddress: string,
    from: string,
    amount: string,
    provider: ethers.Provider
  ): Promise<boolean> {
    try {
      // Get the Trustline compliance module address
      const complianceModuleAddress = await getTrustlineComplianceModuleAddress(provider, contractAddress);
      
      // Build raw ABI-encoded data matching the compliance module
      const contextData = this.buildBurnContext(contractAddress, from, amount);
      
      const validationData: TrustlineWeb3ValidateParams = {
        chainId: chainId.toString(),
        senderAddress: from, // The actor/subject is the from address
        contractAddress: complianceModuleAddress, // Use compliance module address
        nativeAmount: '0',
        data: contextData, // Raw ABI-encoded context data
        validationMode: 'erc3643'
      };

      const response = await this.validateTransaction(validationData);

      if ('error' in response) {
        console.error('Trustline validation error:', response.error);
        return false;
      }

      switch (response.result.status) {
        case 'approved':
          console.log('Burn transaction approved by Trustline');
          return true;
        case 'approval_required':
          console.log('Burn transaction requires manual approval');
          return false;
        case 'rejected':
          console.log('Burn transaction rejected by Trustline:', response.result.reason);
          return false;
        default:
          console.log('Unknown validation status:', (response.result as any).status);
          return false;
      }
    } catch (error) {
      console.error('Trustline validation failed:', error);
      return false;
    }
  }
}

export const trustlineService = new TrustlineService();
